import { memo, type SVGProps } from 'react'

import { IconImage } from '@/components/icons/IconImage'
import { ICONS_FA, ICONS_MTG, ICONS_SVG, type IconType, isIconFaType, isIconMtgType, isIconSvgType } from '@/defs/icon'
import { type DistributiveOmit } from '@/defs/misc'
import { cn } from '@/utils/commonUtil'

import styles from './Icon.module.scss'

type Props = (
  | {
      name: IconType
      src?: never
    }
  | {
      name?: never
      src: string
    }
) &
  DistributiveOmit<SVGProps<SVGSVGElement>, 'icon' | 'className'>

export const IconInner = ({ name, src, ...props }: Props) => {
  if (isIconFaType(name)) {
    const [width, height, , , path] = ICONS_FA[name].icon

    return (
      <svg
        {...props}
        aria-hidden
        className={cn(styles.container, { styles, variant: 'vector' })}
        focusable='false'
        role='img'
        viewBox={`0 0 ${width} ${height}`}
      >
        {Array.isArray(path) ? (
          path.map((p, i) => <path d={p} fill='currentColor' key={i} />)
        ) : (
          <path d={path} fill='currentColor' />
        )}
      </svg>
    )
  }

  if (isIconSvgType(name)) {
    const Element = ICONS_SVG[name]
    return <Element {...props} className={cn(styles.container, { styles, variant: 'vector' })} />
  }

  if (isIconMtgType(name)) {
    return (
      <IconImage
        alt={`{${name.replace('mtg-', '')}}`}
        className={cn(styles.container, { styles, variant: 'image' })}
        src={ICONS_MTG[name].src}
      />
    )
  }

  if (src) {
    return <IconImage className={cn(styles.container, { styles, variant: 'image' })} src={src} />
  }

  return null
}

export const Icon = memo(IconInner)
